<template>
  <section>
    <LmComponentLoading v-if="isLoading" />
    <div v-if="EChartSettings">
      <v-chart autoresize :key="key" class="chart" :option="EChartSettings" :init-options="initOptions" :style="'min-height:' + widget.height + 'px'" />
    </div>
    <div v-else>
      <LmSvgIcon
        v-if="error"
        wrapperClass="d-flex h-100 justify-content-center align-items-center flex-column"
        width="12em"
        icon="LmErrorIcon"
        text="No pudimos cargar el Widget."
      />
      <template v-else>
        <h4>Configura tu gráfico</h4>
        <p>Comienza a configurar tu gráfico desde el panel izquierdo para poder visualizarlo aquí mismo.</p>
      </template>
    </div>
  </section>
</template>

<script>
// Component
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'
import 'zrender/lib/svg/svg'

// Utils
import commonsApi from '@/api/commons'

use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent
])

export default {
  components: {
    VChart
  },
  provide: {
    [THEME_KEY]: 'ligth'
  },
  props: {
    widget: {
      type: Object
    },
    customization: {
      type: Object
    },
    hideTitle: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      error: false,
      isLoading: false,
      EChartSettings: null,
      initOptions: {
        renderer: 'svg'
      },
      key: 0
    }
  },
  methods: {
    checkSize (size) {
      // Agrega retrocompatibilidad en size, size por defecto es 10
      if (!size) {
        size = 10
      }
      return size
    },
    renderData (data) {
      const namesArr = data.map(el => el.name)
      const valuesArr = data.map(el => el.value)
      const seriesArr = data.map(el => ({ name: el.name, type: 'bar' }))
      const title = this.widget.title || this.widget.datatableAlias
      // Vertical
      const xAxisVertical = {
        type: 'category'
      }
      const yAxisVertical = {
        minInterval: 1
      }
      const datasetVertical = {
        source: [
          ['names', ...namesArr],
          [title, ...valuesArr]
        ]
      }
      // Horizontal
      const xAxisHorizontal = {
        minInterval: 1
      }
      const yAxisHorizontal = {
        type: 'category'
      }
      const datasetHorizontal = {
        source: [
          ['name', ...namesArr],
          ['', ...valuesArr]
        ]
      }

      this.EChartSettings = {
        grid: {
          // top: this.widget.orient === 'horizontal' ? 85 : 48,
          top: this.widget.gridTop,
          right: this.widget.gridRight >= 0 ? this.widget.gridRight + '%' : 'auto',
          // width: this.widget.orient === 'horizontal' ? 'auto' : '60%',
          width: this.widget.gridWidth ? this.widget.gridWidth + '%' : 'auto',
          // right: this.widget.orient === 'horizontal' ? '10%' : '0',
          containLabel: false
        },
        // Propiedades para modificar el tooltip
        // https://echarts.apache.org/en/option.html#tooltip.formatter
        tooltip: {
          formatter: function (params) {
            const name = params.name === '' ? params.seriesName : params.name
            const seriesName = params.name !== '' ? params.seriesName : ''
            return `
              ${name}<br />
              <div>
                <span style="color: ${params.color}">&#11044;</span>
                ${seriesName}
                <b style="float: right; margin-left: 20px">${params.data[params.seriesIndex + 1]}</b>
              </div>
            `
          }
        },
        legend: {
          type: this.widget.scroll,
          orient: this.widget.orient,
          left: 'left',
          top: this.widget.top,
          itemWidth: 12,
          itemHeight: 12,
          icon: this.widget.icon,
          itemGap: this.widget.itemGap,
          textStyle: {
            // fontSize: 16
            overflow: this.widget.overflow,
            ellipsis: '...',
            width: this.widget.width > 10 ? this.widget.width : null,
            lineHeight: 18
          },
          data: namesArr
        },
        xAxis: this.widget.gridOrient === 'horizontal' ? xAxisHorizontal : xAxisVertical,
        yAxis: this.widget.gridOrient === 'horizontal' ? yAxisHorizontal : yAxisVertical,
        dataset: this.widget.gridOrient === 'horizontal' ? datasetHorizontal : datasetVertical,
        series: seriesArr
      }
      if (!this.hideTitle) {
        this.EChartSettings.title = {
          text: title,
          left: 'center'
        }
      }
      // NOTA. Fix temporal solo para modo edicion al cambiar entre aggsToRender
      this.key++
    },
    async loadData (datatable, term, size) {
      try {
        this.isLoading = true
        this.error = false
        const res = await commonsApi.axiosPostWrapper('/widgets/generate-chart', {
          id: datatable.id,
          term: term,
          size: this.checkSize(size)
        })

        if (!this.widget.title) {
          this.widget.title = res.data.title
        }

        this.renderData(res.data.items)
      } catch (error) {
        console.log(error)
        this.EChartSettings = null
        this.error = true
      } finally {
        this.isLoading = false
      }
    }
  },
  watch: {
    widget: {
      deep: true,
      async handler (val) {
        if (val.aggTerm && val.dataOrigin) {
          await this.loadData(val.dataOrigin, val.aggTerm, val.size)
        } else {
          this.EChartSettings = null
        }
      }
    }
  },
  async created () {
    try {
      this.isLoading = true
      if (this.widget.dataOrigin && this.widget.aggTerm) {
        await this.loadData(this.widget.dataOrigin, this.widget.aggTerm, this.widget.size)
      } else {
        this.EChartSettings = null
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  }
}
</script>

<style lang="sass" scoped>
.chart
  //
</style>
